/**
 * Role-scoped activity timeline — full paginated feed from /api/v1/dashboard/activity.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { parseServerDate, fmtDateIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityFeedRow } from '@/components/ActivityFeedRow';
import { DataTablePagination, usePagedQueryParams } from '@/components/DataTablePagination';

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = parseServerDate(iso);
    if (!d) return iso;
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return fmtDateIST(d, { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

export default function ActivityCenter() {
  const { params, setPage, setPageSize } = usePagedQueryParams({ defaultPageSize: 50 });
  const qs = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['v1-dashboard-activity', qs.toString()],
    queryFn: () => v1Get(`/dashboard/activity?${qs.toString()}`),
    keepPreviousData: true,
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Activity"
        subtitle="Recent candidate events in your scope — links sent, files uploaded, reports shared, and assessments completed."
      />

      <Card className="card-elevated">
        <CardContent className="p-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive" data-testid="activity-center-error">
              Could not load activity.
            </p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="activity-center-empty">
              No activity recorded in your scope yet.
            </p>
          ) : (
            <ul className="divide-y" data-testid="activity-center-list">
              {items.map((item) => (
                <li key={item.id}>
                  <ActivityFeedRow
                    item={item}
                    fmtTs={fmtTs}
                    testId={`activity-center-row-${item.id}`}
                  />
                </li>
              ))}
            </ul>
          )}

          {!isLoading && !isError && (data?.total || 0) > 0 && (
            <div className="mt-4 border-t pt-4">
              <DataTablePagination
                page={data.page}
                pageSize={data.page_size}
                total={data.total}
                totalPages={data.total_pages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[25, 50, 100]}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
